// --------------------------------------------------------------
// Created On: 2024-06-11
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useEffect, useState } from "react";
import styles from "./ShoppingCartButton.module.scss";
import { MS_PER_SECOND } from "../../../constants/miscellaneous";
import { useNavigate } from "react-router-dom";
import useRefreshCart from "../../../hooks/useRefreshCart";
import useLastActive from "../../../hooks/useLastActive";
import useReduxSelector from "src/hooks/useReduxSelector";
import {
  SHOPPING_CART_MAX_INACTIVE_USER_SECONDS,
  SHOPPING_CART_POLLING_DELAY_SECONDS,
} from "../../../constants/polling";

export default function ShoppingCartButton(): Component {
  const [counter, setCounter] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [quantityText, setQuantityText] = useState<string>("");
  const navigate = useNavigate();
  const quantity = useReduxSelector((state) => state.cartQuantity);
  const lastRetrievedApiQuantity = useRefreshCart(() => true, [dataPollCounter]);
  const failedToLoad = lastRetrievedApiQuantity === null;
  const userInactiveSeconds = useLastActive(SHOPPING_CART_POLLING_DELAY_SECONDS);

  // Updates the string representation of the current user's cart quantity if the data poll counter has been reset
  // by quantity changes.
  // If the request to the API endpoint fails, the badge should display an exclamation point.
  useEffect(() => {
    let newText = "";
    if (failedToLoad) {
      newText = "!";
    } else if (quantity >= 100) {
      newText = "99+";
    } else {
      newText = `${quantity}`;
    }
    setQuantityText(newText);
  }, [quantity, failedToLoad]);

  // Periodically increment the counter that will trigger refreshing the cart.
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setCounter((previousCounter) => previousCounter + 1);
      // Only poll data if the user is active.
      console.log(
        `Looking to see if shopping cart data should be polled` +
          ` (inactive for ${userInactiveSeconds} seconds of ${SHOPPING_CART_MAX_INACTIVE_USER_SECONDS} second limit).`
      );
      if (userInactiveSeconds < SHOPPING_CART_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Data will not be polled.");
      }
    }, SHOPPING_CART_POLLING_DELAY_SECONDS * MS_PER_SECOND);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [counter]);

  return (
    <button
      data-test="shopping-cart-btn"
      className={styles.btn}
      type="button"
      onClick={() => navigate("/shopping-cart")}
    >
      {(quantity > 0 || failedToLoad) && (
        <span data-test="shopping-cart-badge" className={styles.badge}>
          {quantityText}
        </span>
      )}
      <i className={`${styles.shoppingCart} fa fa-2x fa-fw fa-shopping-cart`} />
    </button>
  );
}
